import { E as EmblaCarousel } from './embla-carousel.esm-DpJzJcqm.js';
import { O as Oe } from './index.esm-CXYreJ4v.js';

/**
 * @typedef {import('embla-carousel').EmblaCarouselType} EmblaCarouselApi
 */

/**
 * Add click handlers to the dots
 * @param {EmblaCarouselApi} emblaApi - The embla api
 * @param {HTMLElement} dotsNode - The dots node
 */
function emblaPaginationDots(emblaApi, dotsNode) {
  let dotNodes = [];

  const addDotBtnsWithClickHandlers = () => {
    dotsNode.innerHTML = emblaApi
      .snapList()
      .map(() => '<button class="embla__dot" type="button"></button>')
      .join('');

    dotNodes = Array.from(dotsNode.querySelectorAll('.embla__dot'));
    dotNodes.forEach((dotNode, index) => {
      dotNode.addEventListener('click', () => emblaApi.goTo(index), false);
    });
  };

  const toggleDotButtonsActive = () => {
    const previous = emblaApi.previousSnap();
    const selected = emblaApi.selectedSnap();
    dotNodes.at(previous)?.classList.remove('embla__dot--selected');
    dotNodes.at(selected)?.classList.add('embla__dot--selected');
  };

  addDotBtnsWithClickHandlers();
  toggleDotButtonsActive();

  emblaApi
    .on('reinit', () => { addDotBtnsWithClickHandlers(); toggleDotButtonsActive(); })
    .on('select', toggleDotButtonsActive);
}

/**
 * @typedef {import('embla-carousel').EmblaCarouselType} EmblaCarouselApi
 */

/**
 * Drive a scrollbar thumb: its width is one slide's share of the track and it
 * sits on the segment of the active slide. Index-based (not continuous) so it
 * stays clean when the carousel loops — it jumps to the active slide's segment
 * instead of sliding backwards across the track on wrap-around.
 * @param {EmblaCarouselApi} emblaApi - The embla api
 * @param {HTMLElement} thumbNode - The scrollbar thumb element
 */
function emblaScrollbar(emblaApi, thumbNode) {
  const size = () => {
    thumbNode.style.width = `${(1 / Math.max(emblaApi.snapList().length, 1)) * 100}%`;
  };

  // translateX is relative to the thumb's own width, so one slide of travel is
  // exactly 100% — the thumb lands on the active slide's segment of the track.
  const move = () => {
    thumbNode.style.transform = `translateX(${emblaApi.selectedSnap() * 100}%)`;
  };

  const update = () => {
    size();
    move();
  };

  update();
  emblaApi.on('select', move).on('reinit', update);
}

const WHEEL_MIN_PX = 20;
// Short debounce so one physical click can't fire multiple advances, while
// still allowing every deliberate tick to advance a slide (250ms << 1 tick interval).
const WHEEL_DEBOUNCE_MS = 250;

class ProductMediaSlider extends HTMLElement {
  #viewport = this.querySelector('.embla__viewport');
  #dots = this.querySelector('.embla__dots');
  #scrollbarThumb = this.querySelector('.embla__scrollbar__thumb');
  /** @type {import('embla-carousel').EmblaCarouselType} */ #embla;
  #wheelTimer = null;
  #desktop = window.matchMedia('(min-width: 1024px)');

  connectedCallback() {
    this.#initCarousel();
    this.#initFancybox();
  }

  disconnectedCallback() {
    clearTimeout(this.#wheelTimer);
    this.#viewport?.removeEventListener('wheel', this.#onWheel);
    this.#embla?.destroy();
  }

  #initCarousel() {
    if (!this.#viewport) return;

    this.#embla = EmblaCarousel(this.#viewport, {
      loop: true,
      skipSnaps: true,
      breakpoints: {
        '(min-width: 1024px)': { axis: 'y', loop: false, duration: 15, skipSnaps: false },
      },
    });

    this.#viewport.addEventListener('wheel', this.#onWheel, { passive: false });

    emblaPaginationDots(this.#embla, this.#dots);
    emblaScrollbar(this.#embla, this.#scrollbarThumb);
  }

  /**
   * Desktop-only. Each discrete wheel tick advances one slide immediately,
   * without waiting for the current animation to settle. Embla handles
   * overlapping animations natively. A short debounce prevents sub-events
   * within a single physical click from firing twice.
   * Releases to the page at the first/last slide (releaseOnEdges).
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    if (!this.#desktop.matches) return;

    const delta = this.#wheelToPixels(event);
    if (delta === 0) return;

    const forward = delta > 0;

    // Release to page scroll when exhausted in this direction.
    if (forward ? !this.#embla.canGoToNext() : !this.#embla.canGoToPrev()) return;

    // Carousel owns this direction — prevent page scroll for all events,
    // including sub-threshold ones that won't advance a slide.
    event.preventDefault();

    if (Math.abs(delta) < WHEEL_MIN_PX) return;

    if (this.#wheelTimer !== null) return;
    this.#wheelTimer = setTimeout(() => { this.#wheelTimer = null; }, WHEEL_DEBOUNCE_MS);

    // Zero out residual scroll velocity before advancing. goToNext() stacks on
    // top of whatever velocity the previous animation left, which overshoots the
    // snap and bounces back. One frame at duration=0 flushes velocity to zero.
    this.#embla.internalEngine().scrollBody.useDuration(0);
    requestAnimationFrame(() => {
      if (forward) this.#embla.goToNext();
      else this.#embla.goToPrev();
    });
  };

  /**
   * Wheel delta in pixels — Firefox reports it in lines (deltaMode 1).
   * @param {WheelEvent} event
   * @returns {number}
   */
  #wheelToPixels(event) {
    return event.deltaMode === 1 ? event.deltaY * 16 : event.deltaY;
  }

  #initFancybox() {
    const { chevronLeft, chevronRight, xMark, plus, minus, arrowTurn } = window.icons.regular;

    Oe.bind('[data-fancybox]', {
      idle: false,
      contentClick: 'toggleCover',
      wheel: 'slide',

      // Toolbar
      Toolbar: {
        absolute: true,
        display: {
          left: ['prev', 'close'],
          middle: ['zoomInCustom', 'zoomOutCustom', 'resetCustom'],
          right: ['next'],
        },

        items: {
          prev: {
            tpl: `<button class="f-button" title="{{PREV}}" data-fancybox-prev>${chevronLeft}</button>`,
          },
          next: {
            tpl: `<button class="f-button" title="{{NEXT}}" data-fancybox-next>${chevronRight}</button>`,
          },
          close: {
            tpl: `<button class="f-button" title="{{CLOSE}}" data-fancybox-close>${xMark}</button>`,
          },

          // The following items are not default Fancybox items, so we have to add them custom
          zoomInCustom: {
            tpl: `<button class="f-button" title="Zoom In" data-panzoom-action="zoomIn">${plus}</button>`,
          },
          zoomOutCustom: {
            tpl: `<button class="f-button" title="Zoom Out" data-panzoom-action="zoomOut">${minus}</button>`,
          },
          resetCustom: {
            tpl: `<button class="f-button" title="Reset" data-panzoom-action="reset">${arrowTurn}</button>`,
          },
        },
      },

      // Thumbs
      Thumbs: {
        type: 'classic',
        Carousel: {
          enabled: false,
          breakpoints: {
            '(min-width: 768px)': {
              enabled: true,
              axis: 'y',
              slidesPerPage: 1,
              center: true,
              fill: true,
              dragFree: true,
              Navigation: {
                prevTpl: chevronLeft,
                nextTpl: chevronRight,
              },
            },
          },
        },
      },

      // Images
      Images: {
        Panzoom: {
          maxScale: 5,
          step: 1.2,
        },
      },

      Html: {
        videoTpl: `<video class="fancybox__html5video" loop muted playsinline controls controlsList="nodownload" poster="{{poster}}"><source src="{{src}}" type="{{format}}" />Sorry, your browser doesn't support embedded videos.</video>`,
      },

      // Carousel
      Carousel: {
        infinite: true,
        Navigation: false,
        Panzoom: {
          touch: (event) => event?.target.nodeName !== 'MODEL-VIEWER',
        },
      },
    });
  }
}

customElements.define('product-media-slider', ProductMediaSlider);
